import React, { useState, useEffect } from 'react';
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import mapStyles from '../config/mapStyles.json';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import { api } from 'common';
import { colors } from 'components/Theme/WebTheme';
import { FONT_FAMILY } from 'common/sharedFunctions';
import CircularLoading from '../components/CircularLoading';
import { DollarSign, Users, Car, TrendingUp } from 'lucide-react';

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);
    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const dispatch = useDispatch();
    const { fetchDrivers, clearFetchDrivers } = api;

    function formatAmount(value, decimal, country) {
        const number = parseFloat(value || 0);
        if (country === "Vietnam") {
            return number.toLocaleString("vi-VN", { minimumFractionDigits: decimal, maximumFractionDigits: decimal });
        } else {
            return number.toLocaleString("en-US", { minimumFractionDigits: decimal, maximumFractionDigits: decimal });
        }
    }

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => { dispatch(clearFetchDrivers()); };
    }, [dispatch, fetchDrivers, clearFetchDrivers]);

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({ lat: position.coords.latitude, lng: position.coords.longitude }),
                error => console.log(error)
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (settingsdata.settings) setSettings(settingsdata.settings);
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED');
            const drivers = usersdata.drivers;
            let locs = [];
            for (let i = 0; i < drivers.length; i++) {
                if (drivers[i].location) {
                    let carImage = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
                    let bookingRef = null;
                    for (let j = 0; j < cars.length; j++) {
                        if (cars[j].name === drivers[i].carType) carImage = cars[j].image;
                    }
                    for (let j = 0; j < liveBookings.length; j++) {
                        if (liveBookings[j].driver === drivers[i].uid) bookingRef = liveBookings[j].reference;
                    }
                    locs.push({
                        id: i, lat: drivers[i].location.lat, lng: drivers[i].location.lng,
                        drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                        carnumber: drivers[i].vehicleNumber, cartype: drivers[i].carType,
                        carImage, bookingRef, fleetadmin: drivers[i].fleetadmin
                    });
                }
            }
            if (auth.profile.usertype === 'fleetadmin') {
                locs = locs.filter(driver => driver.fleetadmin === auth.profile.uid);
            }
            setLocations(locs);
        }
    }, [usersdata.drivers, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, settings]);

    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0, yearlyTotal = 0, todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') &&
                    ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin') || auth.profile.usertype === 'admin')) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear())
                            todayTotal += parseFloat(convenience_fees) - parseFloat(discount);
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear())
                            monthlyTotal += parseFloat(convenience_fees) - parseFloat(discount);
                        yearlyTotal += parseFloat(convenience_fees) - parseFloat(discount);
                    }
                    if (fleetCommission && parseFloat(fleetCommission) > 0 && auth.profile.usertype === 'fleetadmin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear())
                            todayTotal += parseFloat(fleetCommission);
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear())
                            monthlyTotal += parseFloat(fleetCommission);
                        yearlyTotal += parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [bookinglistdata.bookings, settings, auth.profile.uid, auth.profile.usertype]);

    useEffect(() => {
        if (usersdata.users) {
            let dc = 0, ac = 0, cc = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                const u = usersdata.users[i];
                const isMyUser = (auth.profile.usertype === 'fleetadmin' && u.fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin';
                if (u.usertype === 'driver' && isMyUser) dc++;
                if (u.driverActiveStatus === true && isMyUser) ac++;
                if (u.usertype === 'customer' && isMyUser) cc++;
            }
            setActiveCount(ac); setDriverCount(dc); setCustomerCount(cc);
        } else { setActiveCount(0); setDriverCount(0); setCustomerCount(0); }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        let completeArr = [], cancelArr = [];
        const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let cc = 0, xc = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const tDate = new Date(bookinglistdata.bookings[j].tripdate);
                    if (months[i] === months[tDate.getMonth()]) {
                        if (bookinglistdata.bookings[j].status === 'PAID' || bookinglistdata.bookings[j].status === 'COMPLETE') cc++;
                        if (bookinglistdata.bookings[j].status === 'CANCELLED') xc++;
                    }
                }
                completeArr.push(cc); cancelArr.push(xc);
            }
        }
        setAllCompleteCount(completeArr); setAllCancelCount(cancelArr);
    }, [bookinglistdata.bookings]);

    const barChartOptions = {
        labels: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
        colors: [colors.GREEN, colors.RED],
        options: { maintainAspectRatio: false, dataLabels: { enabled: false }, stroke: { curve: 'smooth' } },
        chart: { height: 350, type: 'area', fontFamily: FONT_FAMILY },
        xaxis: { labels: { style: { fontFamily: FONT_FAMILY } } },
        yaxis: { labels: { style: { fontFamily: FONT_FAMILY } } },
        title: { style: { fontFamily: FONT_FAMILY } },
        subtitle: { style: { fontFamily: FONT_FAMILY } },
        tooltip: { style: { fontFamily: FONT_FAMILY } },
        legend: { fontFamily: FONT_FAMILY }
    };

    const barChart = [
        { name: t('completed_bookings'), data: allCompleteCount },
        { name: t('cancelled_bookings'), data: allCancelCount }
    ];

    const statCards = [
        { title: t('today_text'), value: settings.swipe_symbol === false ? `${settings.symbol} ${formatAmount(dailygross, settings.decimal, settings.country)}` : `${formatAmount(dailygross, settings.decimal, settings.country)} ${settings.symbol}`, icon: DollarSign, color: 'from-blue-500 to-blue-600' },
        { title: t('this_month_text'), value: settings.swipe_symbol === false ? `${settings.symbol} ${formatAmount(monthlygross, settings.decimal, settings.country)}` : `${formatAmount(monthlygross, settings.decimal, settings.country)} ${settings.symbol}`, icon: TrendingUp, color: 'from-purple-500 to-purple-600' },
        { title: t('total'), value: settings.swipe_symbol === false ? `${settings.symbol} ${formatAmount(totalgross, settings.decimal, settings.country)}` : `${formatAmount(totalgross, settings.decimal, settings.country)} ${settings.symbol}`, icon: DollarSign, color: 'from-green-500 to-green-600' },
    ];

    const countCards = [
        { title: t('total_cumtomer'), value: customerCount, color: 'from-blue-500 to-blue-600', icon: Users },
        { title: t('total_drivers'), value: driverCount, color: 'from-purple-500 to-purple-600', icon: Car },
        { title: t('active_driver'), value: activeCount, color: 'from-green-500 to-green-600', icon: Car },
    ];

    return bookinglistdata.loading || usersdata.loading ? <CircularLoading /> : (
        <div className="p-4 space-y-6" style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
            {/* Page Title */}
            <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: FONT_FAMILY }}>
                {t('gross_earning')}
            </h1>

            {/* Earnings Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {statCards.map((card, i) => (
                    <div key={i} className={`bg-gradient-to-br ${card.color} rounded-2xl p-5 text-white shadow-lg`}>
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium opacity-90" style={{ fontFamily: FONT_FAMILY }}>{card.title}</p>
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                <card.icon className="w-5 h-5" />
                            </div>
                        </div>
                        <p className="text-2xl font-bold" style={{ fontFamily: FONT_FAMILY }}>{card.value}</p>
                    </div>
                ))}
            </div>

            {/* Map + Count Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Map */}
                <div className="lg:col-span-3 bg-white rounded-2xl shadow-lg overflow-hidden">
                    <h2 className="text-lg font-semibold text-gray-800 p-4 border-b border-gray-100" style={{ fontFamily: FONT_FAMILY }}>
                        {t('real_time_driver_section_text')}
                    </h2>
                    {mylocation && mylocation.lat ? (
                        <GoogleMap
                            zoom={10}
                            center={mylocation}
                            mapContainerStyle={{ height: '380px', width: '100%', borderRadius: '16px' }}
                            options={{ styles: mapStyles.light, disableDefaultUI: true, zoomControl: true }}
                        >
                            {locations.map(marker => (
                                <Marker
                                    key={marker.id}
                                    position={{ lat: marker.lat, lng: marker.lng }}
                                    icon={{ url: marker.carImage, scaledSize: new window.google.maps.Size(35, 25) }}
                                >
                                    <InfoWindow
                                        position={{ lat: marker.lat, lng: marker.lng }}
                                        options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                    >
                                        <div style={{ fontFamily: FONT_FAMILY }}>{marker.drivername}<br />{marker.carnumber}</div>
                                    </InfoWindow>
                                </Marker>
                            ))}
                        </GoogleMap>
                    ) : <div className="h-[380px] flex items-center justify-center text-gray-400">Loading map...</div>}
                </div>

                {/* Count Cards */}
                <div className="flex flex-col gap-4">
                    {countCards.map((card, i) => (
                        <div key={i} className={`bg-gradient-to-br ${card.color} rounded-2xl p-5 text-white shadow-lg flex-1 flex flex-col items-center justify-center`}>
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mb-2">
                                <card.icon className="w-5 h-5" />
                            </div>
                            <p className="text-sm font-medium opacity-90 text-center" style={{ fontFamily: FONT_FAMILY }}>{card.title}</p>
                            <p className="text-3xl font-bold mt-1" style={{ fontFamily: FONT_FAMILY }}>{card.value}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Chart */}
            <div className="bg-white rounded-2xl shadow-lg p-4">
                <h2 className="text-lg font-semibold mb-2" style={{ color: colors.GREEN, fontFamily: FONT_FAMILY }}>
                    {t('booking_chart')}
                </h2>
                <Chart options={barChartOptions} series={barChart} type="area" width="100%" height={360} />
            </div>
        </div>
    );
};

export default Dashboard;
